import * as API from '@/API'

const createState = () => ({
  isLoading: false,
  location: [],
  locationSchema: {},
  locationByCountry: [],
  serviceLogs: [],
  circuit: [],
  circuitSchema: {},
  crossconnect: [],
  crossconnectSchema: {},
  device: [],
  deviceSchema: {},
  'ip-transit-smtp-exception': [],
  'ip-transit-smtp-exceptionSchema': {},
  'ip-transit-blackhole': [],
  'ip-transit-blackholeSchema': {},
  'ip-transit-network': [],
  'ip-transit-networkSchema': {},
  'ip-network-group': [],
  colocation: [],
  colocationSchema: {},
  'location-country': [],
  'location-metro': [],
  'location-metroSchema': {},
  organisation: [],
  organisationSchema: {},
  'patch-panel': [],
  'patch-panelSchema': {},
  'bgp-connection': [],
  'bgp-connectionSchema': {},
  circuitInterfaces: {},
  'finance/beneficiary': [],
  'finance/legal-entity': [],
  'finance/product': [],
  'finance/charge/network-component': [],
  'finance/chargeSchema': [],
  locationProducts: [],
  'supplier-rate-cardSchema': {},
  'virtual-machine-image': [],
  'virtual-machine-imageSchema': {},
  'virtual-machine-init-format': [],
})

const actions = {
  getResourceSchema: async ({ commit }, resourceName) => {
    try {
      const results = await API.getResourceSchema(resourceName)
      commit('setResourceSchema', {
        key: resourceName,
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getResourceList: async ({ commit }, reqParams) => {
    // overloading reqParams (can be single value or object)
    let name = reqParams, params = {}
    if (typeof reqParams === 'object') {
      name = reqParams.name
      params = reqParams.params
    }
    try {
      const results = await API.getResource({ name: name, params: params })
      commit('setResourceValue', {
        key: name,
        value: results,
      })
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getFilteredResourceList: async (_, reqParams) => {
    let name = reqParams, params = {}
    if (typeof reqParams === 'object') {
      name = reqParams.name
      params = reqParams.params
    }
    try {
      const results = await API.getResource({ name: name, params: params })
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getResourceById: async ({ commit }, resource) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.getResourceById(resource.type, resource.id)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },

  getProductsByLocation: async ({ commit }, id) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.getProductsByLocationId(id)
      commit('setResourceValue', {
        key: 'locationProducts',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },

  addLocationProduct: async ({ commit }, data) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.addLocationProduct(data)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },

  removeLocationProduct: async ({ commit }, id) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.removeLocationProduct(id)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },

  enablePortDiversity: async (_, payload) => {
    try {
      const results = await API.enablePortDiversity(payload.id, payload.port_speed )
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  postResourceById: async ({ dispatch }, resource) => {
    try {
      const results = await API.postResourceById(resource.type, resource.id, resource.body, resource.params)
      if (resource.type === 'crossconnect') {
        return dispatch('crossConnectOptions', { resource: resource.body, crossconnect: results, new: false })
      }

      if (resource.type === 'circuit') {
        return dispatch('linkInterfaceOptions', { resource: resource.body, circuit: results, newCircuit: false })
      }
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deleteResourceById: async (_, resource) => {
    try {
      const results = await API.deleteResourceById(resource.type, resource.id)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  createResource: async ({ dispatch }, resource) => {
    try {
      const results = await API.createResource(resource.type, resource.body)
      if (resource.type === 'crossconnect') {
        return dispatch('crossConnectOptions', { resource: resource.body, crossconnect: results, new: true })
      }
      if (resource.type === 'circuit') {
        return dispatch('linkInterfaceOptions', { resource: resource.body, circuit: results, newCircuit: true })
      }
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  linkInterfaceOptions: async ({ getters }, { circuit, resource, newCircuit }) => {
    const interfaceProperty = 'a_interfaces' in resource || 'b_interfaces' in resource
    if (!interfaceProperty) return Promise.resolve(circuit)
    let interfaceIds = {}
    if (newCircuit) {
      const { a_interfaces = [], b_interfaces = [] } = resource
      interfaceIds = { a_interfaces, b_interfaces }
    } else {
      const {
        a_interfaces = getters.circuitInterfacesValuesByEnd.a_interfaces,
        b_interfaces = getters.circuitInterfacesValuesByEnd.b_interfaces,
      } = resource
      interfaceIds = { a_interfaces, b_interfaces }
    }
    try {
      const results = await API.interfaceCircuit(circuit.id, 'replace', interfaceIds)
      return Promise.resolve({ ...circuit, ...results })
    } catch (error) {
      return Promise.reject(error)
    }
  },

  crossConnectOptions: async ({ dispatch }, payload) => {
    const crossConnectId = payload.new ? payload.crossconnect.id : payload.resource.id

    // In case of no service
    if (payload.new && !payload.resource.service_id) return Promise.resolve(payload.crossconnect)
    // New service
    if (payload.new && payload.resource.service_id) {
      return dispatch('attachCrossConnect', {
        serviceId: payload.resource.service_id,
        crossConnectId: crossConnectId,
        crossConnect: payload.crossconnect,
      })
    }

    // Existing service (update or delete)
    if (!payload.new) {
      const existingServiceId = payload.crossconnect.service_id
      const serviceIdChanged = payload.resource.service_id !== existingServiceId
      const deleteCrossConnect = !payload.resource.service_id

      /* This method is updating the crossconnect serviceId, so if it hasn't changed, return early */
      if (!serviceIdChanged) return Promise.resolve(payload.crossconnect)

      /* If service_id is empty in payload, we need to delete the serviceId from this crossconnect */
      if (deleteCrossConnect) {
        return dispatch('deleteCrossConnect', {
          serviceId: existingServiceId,
          crossConnectId: crossConnectId,
        })
      }

      /* 
       * The existing serviceId must be disconnected from the crossconnect first, 
       * if updating from a previous serviceId. Otherwise the attach call will fail
       */
      if (existingServiceId) {
        await dispatch('deleteCrossConnect', {
            serviceId: existingServiceId,
            crossConnectId: crossConnectId,
        })
      }

      /* Otherwise just update existing crossconnect with new serviceId */
      return dispatch('attachCrossConnect', {
          serviceId: payload.resource.service_id,
          crossConnectId: crossConnectId,
      })
    }
  },

  attachCrossConnect: async (_, payload) => {
    try {
      const results = await API.crossConnectService('POST', payload.serviceId, payload.crossConnectId)
      return Promise.resolve({ ...results, ...payload.crossConnect })
    } catch (error) {
      return Promise.reject(error)
    }
  },

  deleteCrossConnect: async (_, payload) => {
    try {
      const results = await API.crossConnectService('DELETE', payload.serviceId, payload.crossConnectId)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getLocationsByCountry: async ({ commit }, countryId) => {
    try {
      const results = await API.getLocationsByCountry(countryId)
      commit('setResourceValue', {
        key: 'locationsByCountry',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getServiceLogs: async ({ commit }, serviceId) => {
    try {
      const results = await API.getServiceLogs(serviceId)
      commit('setResourceValue', {
        key: 'serviceLogs',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },

  getNetworkCircuits: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'circuit' })
      commit('setResourceValue', {
        key: 'circuits',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getInterfacesByCircuit: async ({ commit }, circuitId) => {
    commit('setLoadingStatus', true)
    try {
      const results = await API.getCircuitInterfaces(circuitId)
      commit('setResourceValue', {
        key: 'circuitInterfaces',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    } finally {
      commit('setLoadingStatus', false)
    }
  },
  getInterfaceListByDevice: async (_, device) => {
    try {
      const results = await API.getInterfacesByDevice(device.deviceId, device.isAvailable)
      return Promise.resolve(results)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getNetworkCrossConnects: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'crossconnect' })
      commit('setResourceValue', {
        key: 'crossconnects',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getNetworkDevices: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'device' })
      commit('setResourceValue', {
        key: 'devices',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getNetworkMetros: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'location-metro' })
      commit('setResourceValue', {
        key: 'metros',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getNetworkOrganisations: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'organisation' })
      commit('setResourceValue', {
        key: 'organisations',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getNetworkPatchPanels: async ({ commit }) => {
    try {
      const results = await API.getResource({ name: 'patch-panel' })
      commit('setResourceValue', {
        key: 'patchPanels',
        value: results,
      })
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },
  setNetworkResource: ({ commit, dispatch }, networkResource) => {
    dispatch('clearNetworkResource')
    commit('setNetworkResource', networkResource)
  },
  clearNetworkResource: ({ commit }) => {
    commit('clearNetworkResource')
  },
  updateNetworkResource: ({ commit }, networkResource) => {
    commit('setResourceValue', {
      key: 'selectedNetworkResource',
      value: networkResource,
    })
    commit('setResourceValue', {
      key: 'selectedNetworkResourceDidMutate',
      value: true,
    })
  },
  clearLocationProducts: ({ commit }) => {
    commit('clearLocationProducts')
  },
}

const getters = {
  locationsById(state) {
    const locationsMap = new Map()
    state.location.forEach(location => {
      locationsMap.set(location.id, location)
    })
    return locationsMap
  },
  locationProducts(state) {
    return state.locationProducts.reduce((acc, item) => {
      acc[item.speed] = item
      return acc
    }, {})
  },
  crossConnectsById(state) {
    const crossconnectsMap = new Map()
    state.crossconnect.forEach(connection => {
      crossconnectsMap.set(connection.id, { name: connection.name, service_id: connection.service_id })
    })
    return crossconnectsMap
  },
  organisationsListOptions(state) {
    const organisations = state.organisation
      .map(organisation => {
        return {
          text: organisation.name,
          value: organisation.id,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    organisations.unshift({
      text: 'None',
      value: null,
    })
    return organisations
  },
  locationCountriesListOptions(state) {
    const countries = state['location-country']
      .map(country => {
        return {
          text: country.name,
          value: country.name,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    countries.unshift({
      text: 'None',
      value: null,
    })
    return countries
  },
  locationListOptions(state) {
    return state.location
      .map(location => {
        const name = location.name || ''
        const siteCode = location.site_code || ''
        let text = ''
        if (siteCode && name) {
          text = `${siteCode} - ${name}`
        } else if (siteCode) {
          text = siteCode
        } else if (name) {
          text = name
        }
        return {
          text,
          value: location.id,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
  },
  deviceListOptions(state) {
    return state.device
      .map(device => {
        return {
          text: `${device.name}`,
          value: device.id,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
  },
  crossconnectListOptions(state) {
    return state.crossconnect
      .map(crossconnect => {
        return {
          text: crossconnect.name,
          value: crossconnect.id,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
  },
  metroListOptions(state) {
    return state['location-metro']
      .map(metro => {
        return {
          text: metro.name,
          value: metro.name,
        }
      })
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
  },
  organisationById: state => organisationId => {
    return state.organisation.find(organisation => organisation.id === organisationId)
  },
  circuitInterfacesByEnd: state => interfaceEnd => {
    if (!Object.prototype.hasOwnProperty.call(state.circuitInterfaces, interfaceEnd)) return []
    return state.circuitInterfaces[interfaceEnd].map(i => {
      return {
        text: `${i.device.name} ${i.name}`,
        value: i.id,
        attached: true,
      }
    })
  },
  circuitInterfacesValuesByEnd(state) {
    return {
      a_interfaces: state.circuitInterfaces.a_interfaces.map(iface => iface.id),
      b_interfaces: state.circuitInterfaces.b_interfaces.map(iface => iface.id),
    }
  },
  financeBeneficiaryOptions(state) {
    return state['finance/beneficiary']
      .slice()
      .sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      .map(item => {
        return { value: item.id, text: item.name }
      })
  },
  financeLegalEntityOptions(state) {
    return state['finance/legal-entity']
      .slice()
      .sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      .map(item => {
        return { value: item.id, text: item.name }
      })
  },
  financialProductOptions(state) {
    return state['finance/product']
      .slice()
      .sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      .map(item => {
        return { value: item.id, text: item.name }
      })
  },
  financialNetworkComponents(state) {
    return state['finance/charge/network-component']
      .slice()
      .sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      .map(item => {
        return { value: item.id, text: item.name }
      })
  },
  virtualMachineInitFormatOptions(state) {
    return state['virtual-machine-init-format']
      .slice()
      .sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      .map(item => {
        return { value: item.id, text: `${item.name} (${item.service_type})` }
      })
  },
}

const mutations = {
  setLoadingStatus: (state, value) => {
    state.isLoading = value
  },
  setResourceValue: (state, nodeObject) => {
    state[nodeObject.key] = nodeObject.value
  },
  setNetworkResource: (state, networkResource) => {
    state.selectedNetworkResource = networkResource.values
    state.selectedNetworkResourceFields = networkResource.fields
    state.selectedNetworkResourceType = networkResource.type
  },
  setResourceSchema: (state, resourceSchema) => {
    const schema = `${resourceSchema.key}Schema`
    state[schema] = resourceSchema.value
  },
  clearNetworkResource: state => {
    state.selectedNetworkResource = null
    state.selectedNetworkResourceType = null
    state.selectedNetworkResourceFields = null
    state.selectedNetworkResourceDidMutate = false
  },
  clearLocationProducts: state => {
    state.locationProducts = []
  },
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state: createState(),
}
